// Nouvelle version de LoaderModal.jsx
import { useMemo } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Spinner } from "react-bootstrap";

const backdropStyle = {
  position: "fixed",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.25)", // 🔴 Plus transparent en mode non-bloquant
  zIndex: 9999,
  WebkitBackdropFilter: "blur(2px)",
  backdropFilter: "blur(2px)",
  pointerEvents: "none", // 🔴 Permet les clics à travers
};

const blockingBackdropStyle = {
  ...backdropStyle,
  background: "rgba(0,0,0,0.45)", // 🔴 Plus opaque en mode bloquant
  pointerEvents: "auto", // 🔴 Bloque les interactions
};

const cardStyle = {
  width: 420,
  maxWidth: "90%",
  borderRadius: 14,
  padding: "20px 22px",
  background: "#fff",
  boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  alignItems: "center",
  pointerEvents: "auto", // 🔴 Toujours interactif
};

const progressContainerStyle = {
  width: "100%",
  height: 8,
  background: "#e9ecef",
  borderRadius: 999,
  overflow: "hidden",
};

const progressBarStyle = {
  height: "100%",
  borderRadius: 999,
  transformOrigin: "left center",
};

const LoaderModal = ({ 
  visible, 
  message = "Veuillez patienter...", 
  autoCloseMs = null, 
  progress, 
  indeterminate,
  nonBlocking = false // 🔴 NOUVEAU : mode non-bloquant
}) => {
  const progressWidth = useMemo(() => {
    if (typeof progress === "number") return `${Math.max(0, Math.min(100, progress))}%`;
    if (autoCloseMs) return "100%";
    return indeterminate ? "60%" : "0%";
  }, [progress, autoCloseMs, indeterminate]);

  // 🔴 Choisir le style selon le mode
  const currentBackdropStyle = nonBlocking ? backdropStyle : blockingBackdropStyle;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {visible && (
        <motion.div
          key="backdrop"
          style={currentBackdropStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            key="card"
            style={cardStyle}
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1, transition: { type: "spring", stiffness: 280, damping: 30 } }}
            exit={{ y: 20, opacity: 0, scale: 0.98, transition: { duration: 0.18 } }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <Spinner
                animation="border"
                role="status"
                variant="primary"
                style={{ width: "2rem", height: "2rem", borderWidth: "0.6rem" }}
              />

              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: 700 }}>{message}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {nonBlocking ? "Traitement en arrière-plan..." : "Cela peut prendre quelques secondes…"}
                </div>
              </div>
            </div>

            <div style={{ width: "100%", marginTop: 8 }}>
              <div style={progressContainerStyle}>
                {autoCloseMs && typeof progress !== "number" ? (
                  <motion.div
                    style={{ ...progressBarStyle, background: "linear-gradient(90deg,#4f46e5,#06b6d4)" }}
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: autoCloseMs / 1000, ease: "linear" }}
                  />
                ) : typeof progress === "number" ? (
                  <motion.div
                    style={{ ...progressBarStyle, background: "linear-gradient(90deg,#4f46e5,#06b6d4)" }}
                    initial={{ width: "0%" }}
                    animate={{ width: progressWidth }}
                    transition={{ duration: 0.35, ease: "easeOut" }}
                  />
                ) : indeterminate ? (
                  <div style={{ position: "relative", height: "100%", overflow: "hidden" }}>
                    <motion.div
                      style={{ position: "absolute", left: "-40%", top: 0, bottom: 0, width: "40%", background: "linear-gradient(90deg,#4f46e5,#06b6d4)", borderRadius: 999 }}
                      animate={{ left: ["-40%", "120%"] }}
                      transition={{ duration: 1.2, repeat: Infinity, ease: "linear" }}
                    />
                  </div>
                ) : (
                  <div style={{ height: "100%" }} />
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, width: "100%", justifyContent: "flex-end", marginTop: 6 }}>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>
                {nonBlocking ? "Vous pouvez continuer à naviguer" : "Merci de patienter"}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default LoaderModal;